import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const services = [
  {
    icon: 'ri-ship-2-line',
    title: 'Importación a Nivel Nacional',
    description: 'Gestionamos el proceso completo de importación de químicos industriales en contenedores de 20\' y 40\', abasteciendo al mercado nacional con los más altos estándares.',
    highlight: 'Importación',
  },
  {
    icon: 'ri-truck-line',
    title: 'Distribución Nacional',
    description: 'Red de distribución a nivel nacional para garantizar la entrega oportuna de nuestros productos a yacimientos y plantas industriales.',
    highlight: 'Cobertura Total',
  },
  {
    icon: 'ri-test-tube-line',
    title: 'Control de Calidad',
    description: 'Cada lote de producción es sometido a rigurosos análisis fisicoquímicos para garantizar que cumpla con las especificaciones API.',
    highlight: 'API Standards',
  },
  {
    icon: 'ri-file-text-line',
    title: 'Documentación Técnica',
    description: 'Proveemos fichas técnicas, hojas de datos de seguridad (MSDS/SDS) y certificados de análisis para cada producto.',
    highlight: 'Certificado',
  },
  {
    icon: 'ri-settings-4-line',
    title: 'Asesoría Técnica',
    description: 'Nuestro equipo de ingenieros especializados ofrece soporte técnico para la formulación y optimización de fluidos de perforación.',
    highlight: 'Expertos',
  },
  {
    icon: 'ri-store-2-line',
    title: 'Almacenamiento Especializado',
    description: 'Instalaciones de almacenamiento con condiciones controladas para preservar la integridad y calidad de los productos químicos.',
    highlight: 'Seguro',
  },
];

const importSlides = [
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/e9c5ae30b52dc3e3cbb207e08eb4d074.jpeg',
    label: 'Contenedor TTNU — Sacos de Producto Químico',
  },
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/ac97c662ce0a78a0b34e3dda83ab2d0d.jpeg',
    label: 'Contenedor TRITON — IBC Tanks Listos para Despacho',
  },
];

export default function Services() {
  const [visible, setVisible] = useState(false);
  const [importSlide, setImportSlide] = useState(0);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const importIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const nextImportSlide = useCallback(() => {
    setImportSlide((c) => (c + 1) % importSlides.length);
  }, []);

  useEffect(() => {
    importIntervalRef.current = setInterval(nextImportSlide, 2000);
    return () => { if (importIntervalRef.current) clearInterval(importIntervalRef.current); };
  }, [nextImportSlide]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="servicios" ref={ref} className="py-20 md:py-28 bg-background-100 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary-500/5 -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-accent-500/5 translate-y-1/2 -translate-x-1/2"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary-500 text-sm font-600 tracking-widest uppercase mb-3 block">
            Lo Que Ofrecemos
          </span>
          <h2 className="font-heading font-700 text-4xl md:text-5xl text-foreground-950 leading-tight mb-4">
            Nuestros <span className="gradient-text">Servicios</span>
          </h2>
          <div className="section-divider mx-auto mb-6"></div>
          <p className="text-foreground-600 text-base max-w-xl mx-auto">
            Soluciones integrales para la industria petrolera, desde el suministro de
            químicos industriales hasta la asesoría técnica especializada.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => (
            <div
              key={service.title}
              className={`group relative p-6 rounded-xl bg-background-50 border border-background-200 card-hover transition-all duration-700 hover:border-primary-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 0.08 + 0.15}s` }}
            >
              {/* Highlight tag */}
              {/* Highlight tag */}
              <div className="absolute top-4 right-4">
                <span className="text-xs font-600 text-primary-500 bg-primary-50 px-2.5 py-1 rounded-full border border-primary-100">
                  {service.highlight}
                </span>
              </div>

              {/* Icon */}
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary-500/10 mb-5 group-hover:bg-primary-500 transition-all duration-300">
                <i className={`${service.icon} text-primary-500 text-2xl group-hover:text-white transition-colors duration-300`}></i>
              </div>

              <h3 className="font-heading font-700 text-xl text-foreground-900 mb-3">
                {service.title}
              </h3>
              <p className="text-foreground-600 text-sm leading-relaxed">
                {service.description}
              </p>

              {/* Bottom accent line */}
              <div className="mt-5 w-0 h-0.5 bg-primary-500 rounded-full group-hover:w-full transition-all duration-500"></div>
            </div>
          ))}
        </div>

        {/* Import Showcase Slideshow */}
        <div className={`mt-14 transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex flex-col md:flex-row gap-0 rounded-2xl overflow-hidden border border-background-200">
            {/* Slideshow */}
            <div className="relative md:w-3/5 h-[480px] md:h-[620px] bg-foreground-950">
              {importSlides.map((slide, si) => (
                <div
                  key={si}
                  className={`absolute inset-0 transition-opacity duration-700 ${si === importSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                >
                  <img
                    src={slide.src}
                    alt={slide.label}
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <span className="absolute bottom-4 left-5 right-5 text-white text-sm font-600 z-10">
                    {slide.label}
                  </span>
                </div>
              ))}
              {/* Dot indicators */}
              <div className="absolute top-4 left-4 z-20 flex gap-2">
                {importSlides.map((_, si) => (
                  <button
                    key={si}
                    onClick={() => setImportSlide(si)}
                    className={`rounded-full transition-all duration-300 cursor-pointer ${
                      si === importSlide ? 'w-6 h-2.5 bg-primary-500' : 'w-2.5 h-2.5 bg-white/50 hover:bg-white/80'
                    }`}
                  />
                ))}
              </div>
              {/* Counter */}
              <div className="absolute top-4 right-4 z-20 px-2.5 py-1 rounded-full bg-black/30 backdrop-blur-sm text-white text-xs font-600">
                {importSlide + 1} / {importSlides.length}
              </div>
            </div>

            {/* Text content */}
            <div className="md:w-2/5 bg-background-50 p-8 md:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10">
                  <i className="ri-ship-2-line text-primary-500 text-xl"></i>
                </div>
                <span className="text-primary-500 text-sm font-600 tracking-widest uppercase">Importación</span>
              </div>
              <h3 className="font-heading font-700 text-2xl md:text-3xl text-foreground-950 mb-4 leading-tight">
                Importación de Químicos<br />a Nivel Nacional
              </h3>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                Gestionamos el proceso completo de importación de químicos industriales en
                <strong className="text-foreground-800"> contenedores de 20&apos; y 40&apos;</strong>, abasteciendo
                al mercado nacional con los más altos estándares de calidad y logística confiable.
              </p>
              <div className="flex flex-col gap-3">
                {['Contenedores 20\' y 40\' con carga completa', 'Sacos, IBC Tanks y tambores industriales', 'Documentación aduanal y certificados incluidos', 'Tiempos de entrega garantizados'].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-check-line text-primary-600 text-xs"></i>
                    </div>
                    <span className="text-foreground-700 text-sm font-500">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA banner */}
        <div className={`mt-14 rounded-2xl bg-primary-500 p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="text-center md:text-left">
            <h3 className="font-heading font-700 text-2xl md:text-3xl text-white mb-2">
              ¿Necesitas Cotización?
            </h3>
            <p className="text-primary-100 text-sm md:text-base">
              Contáctanos hoy y recibe una propuesta personalizada para tu operación.
            </p>
          </div>
          <button
            onClick={() => navigate('/contacto')}
            className="flex-shrink-0 px-8 py-4 bg-white text-primary-600 font-700 rounded-md hover:bg-primary-50 transition-all duration-200 cursor-pointer whitespace-nowrap text-base"
          >
            Solicitar Cotización
          </button>
        </div>
      </div>
    </section>
  );
}